/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import {
    Grid,
    List,
    ListItem,
    ListItemText,
    Typography,
    Paper,
    FormControlLabel,
    Radio,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Box,
    Menu,
    MenuItem,
    Divider
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';

import LoadingButton from '@mui/lab/LoadingButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { API_URL, gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ApiHelper from 'utils/apiHelper';
import AddViewDialog from './AddViewDialog';
import EditViewDialog from './EditViewDialog';
import RightClickOptionsDialog from '../Devices/RightClickOptionsDialog';
import ReportFileExports from 'components/Reports/FileExports';
import EditDeviceColumnsVisibilityDialog from '../Devices/EditDeviceColumnsVisibility';
import { applyColumnStyle, handleDeviceColumns } from 'utils/commonHelpers';
import { getDevices, updateSearchParams } from 'components/../redux/actions/devices';

const ManagementViews = () => {
    const theme = useTheme();
    const gridRef = useRef();
    const { userInfo } = useSelector((state) => state.auth);
    const settings = useSelector((state) => state.settings);
    const [loading, setLoading] = useState(true);
    const [viewsList, setViewsList] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [totalDeviceColumns, setTotalDeviceColumns] = useState([]);
    const [showAddViewDialog, setShowAddViewDialog] = useState(false);
    const [showEditViewDialog, setShowEditViewDialog] = useState(false);
    const [commonData, setCommonData] = useState({});
    const [selectedViewData, setSelectedViewData] = useState(null);
    const [viewStatements, setViewStatements] = useState([]);
    const [selectedViewStatements, setSelectedViewStatements] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [assetViewsData, setAssetViewsData] = useState([]);
    const [viewsTotalLength, setViewsTotalLength] = useState(0);
    const [sortModel, setSortModel] = useState([]);
    const [csvDownloadFileHeaders, setCsvDownloadFileHeaders] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [queryParams, setQueryParams] = useState({});
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: settings?.generalSettings?.showRows ? settings?.generalSettings?.showRows : 25
    });

    const [ruleStatementValues, setRuleStatementValues] = useState({});
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [contextMenu, setContextMenu] = useState(null);
    const [selectedViews, setSelectedViews] = useState([]);
    const [showContextMenuDialog, setShowContextMenuDialog] = useState(false);
    const [rightClickOption, setRightClickOption] = useState('');
    const [isScriptAvailable, setScriptAvailable] = useState(false);

    const [deviceColumnsData, setDeviceColumnsData] = useState([]);
    const [showEditDeviceOrderColumns, setShowEditDeviceOrderColumns] = useState(false);
    const [inputFields, setInputFields] = useState({});
    const [limitLength, setLimitLength] = useState();
    const dispatch = useDispatch();

    const initialViewData = async (selectedItem) => {
        setLoading(true);
        try {
            // Get views list and statements
            const [viewsListRes, viewStatementsRes] = await Promise.all([
                ApiHelper.get('/api/management/views/'),
                ApiHelper.get('/api/management/get_view_statements/')
            ]);

            // Only proceed if we have views
            if (viewsListRes.data.res.length > 0) {
                let initialView;
                if (selectedItem) {
                    initialView = viewsListRes.data.res.find((view) => view.id === selectedItem);
                } else {
                    const maxPriorityRow = viewsListRes.data.res.reduce((max, item) => {
                        return item.enabled_priority > max.enabled_priority ? item : max;
                    }, viewsListRes.data.res[0]); // Initialize with the first element

                    initialView = maxPriorityRow;
                }

                // Update views list and statements
                setInputFields(viewsListRes.data.input_fields);
                setViewsList(viewsListRes.data.res);
                setViewStatements(viewStatementsRes.data);
                setSelectedItem(initialView.id);

                // Get selected view statements
                const selectedStatements = viewStatementsRes.data.filter((statement) => statement.view_id === initialView.id);
                setSelectedViewStatements(selectedStatements);
                setSelectedViewData(initialView);

                // Format statements for API call
                const initialStatements = selectedStatements.map((statement, index) => ({
                    id: index + 1,
                    attribute: statement.deviceattrib,
                    negative: statement.negative === 1,
                    operator: statement.operator_id,
                    value: statement.value,
                    connector: index === selectedStatements.length - 1 ? '1' : statement.connector_id,
                    operatorOptions: commonData.operators
                }));

                // Get devices for initial view
                const timezoneOffSet = new Date().getTimezoneOffset();
                const devicesRes = await ApiHelper.post('/api/management/get_asset_views/', {
                    viewStatements: initialStatements,
                    page_number: paginationModel.page,
                    page_size: paginationModel.pageSize,
                    sort: queryParams.sort,
                    field: queryParams.field,
                    tz: -timezoneOffSet
                });

                setAssetViewsData(devicesRes.data.data);
                setViewsTotalLength(devicesRes.data.total);
            } else {
                setAssetViewsData([]);
                setViewsList([]);
            }
        } catch (err) {
            if (err.response?.status !== 401) {
                toast.error('Failed to fetch views data');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getRuleStatementsData();
        getRuleStatementValuesData();
        getDeviceColumns();
    }, []);

    const getRuleStatementValuesData = () => {
        ApiHelper.get('/api/automation/rule_statement_values/')
            .then((res) => {
                setRuleStatementValues(res.data);
                setInputFields(res.data.input_fields);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get rule statement values data');
                }
            });
    };

    useEffect(() => {
        getRuleStatementsData();
        getRuleStatementValuesData();
        getDeviceColumns();
        const params = {
            keyword: '',
            id: 0,
            sort: '',
            field: '',
            page_size: settings?.generalSettings?.showRows ? settings?.generalSettings?.showRows : 25,
            page_number: 0
        };
        setQueryParams(params);
    }, [settings?.generalSettings?.showRows]);

    useEffect(() => {
        initialViewData(selectedItem);
    }, [paginationModel]);

    const getDeviceColumns = () => {
        ApiHelper.get('/api/management/device_columns/2/')
            .then((res) => {
                setTotalDeviceColumns(res.data.devices);
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data.devices);
                setLimitLength(res.data.limit_length);
                setColumnDefs(columns_order);
                setDeviceColumnsData(columns_visibility);
                const temp = [];
                columns_order.map((data) => {
                    temp.push({ label: data.headerName, key: data.field });
                });
                setCsvDownloadFileHeaders(temp);
            })
            .catch((err) => {
                if (err.response && err.response.status !== 401) {
                    toast.warn('There is device columns data');
                } else {
                    toast.error('Error fetching device columns');
                }
            });
    };

    const getRuleStatementsData = () => {
        const getOperators = ApiHelper.get(API_URL.RULE_STATEMENT_DATA, {}, {}, { type: 'operators' });
        const getConnectors = ApiHelper.get(API_URL.RULE_STATEMENT_DATA, {}, {}, { type: 'connectors' });
        const getView = ApiHelper.get(API_URL.RULE_STATEMENT_DATA, {}, {}, { type: 'view' });
        Promise.all([getOperators, getConnectors, getView])
            .then(([resOperators, resConnectors, resView]) => {
                setCommonData({
                    operators: resOperators.data,
                    connectors: resConnectors.data,
                    view: resView.data
                });
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get view statements data');
                }
            });
    };

    const handleClose = () => {
        if (showAddViewDialog) {
            setShowAddViewDialog(false);
            initialViewData(null);
        }
        if (showEditViewDialog) {
            setShowEditViewDialog(false);
            initialViewData(selectedItem);
        }
    };

    const handleEditView = (item) => {
        setShowEditViewDialog(true);
        // if (selectedItem === item.id) {
        // } else {
        //     toast.info('This is not selected view!');
        // }
    };

    const handleOpenDialog = (id) => {
        setDeleteItemId(id);
        setOpenDialog(true);
        // if (selectedItem === id) {
        // } else {
        //     toast.info('This is not selected view!');
        // }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setDeleteItemId(null);
    };

    const handleConfirmDelete = () => {
        handleDeleteView(deleteItemId);
        handleCloseDialog();
    };

    const handleDeleteView = (id) => {
        ApiHelper.delete(`${API_URL.MANAGEMENT_VIEWS}${id}/`)
            .then((res) => {
                toast.success('Successfully deleted view');
                initialViewData(null);
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Failed to delete view');
            });
    };

    const handleSortModelChange = React.useCallback(
        (newSortModel) => {
            //console.log(newSortModel);
            setSortModel(newSortModel);
            if (newSortModel.length > 0) {
                const params = {
                    sort: newSortModel[0].sort,
                    field: newSortModel[0].field,
                    page_size: paginationModel.pageSize,
                    page_number: 0
                };
                setPaginationModel({ pageSize: paginationModel.pageSize, page: 0 });
                setQueryParams(params);
            }
        },
        [paginationModel]
    );

    const handleSelectionChange = (newSelection) => {
        const ids = new Set(newSelection);
        const selectedViews = assetViewsData.filter((row) => ids.has(row.deviceid));
        setSelectedViews(assetViewsData.filter((row) => ids.has(row.deviceid)));
        setSelectedRows((prevSelected) => {
            // Create a new set to avoid modifying the previous state directly
            const updatedSelected = new Set(prevSelected);
            // Add selected rows from the current page
            newSelection.forEach((id) => {
                updatedSelected.add(id);
            });

            // Remove rows that are deselected from the current page
            assetViewsData.forEach((row) => {
                if (!newSelection.includes(row.deviceid)) {
                    updatedSelected.delete(row.deviceid);
                }
            });
            const selectedArray = Array.from(updatedSelected);

            return selectedArray;
        });
    };

    const getScriptList = () => {
        ApiHelper.get(API_URL.MANAGEMENT_SCRIPTS)
            .then((res) => {
                setScriptAvailable(res.data.script_list.length > 0);
            })
            .catch(() => {
                setScriptAvailable(false);
            });
    };

    const handleContextMenu = (event) => {
        event.preventDefault();
        if (selectedRows.length === 0) {
            toast.warning('Please select the device');
            return true;
        }
        setContextMenu(contextMenu === null ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 } : null);
    };

    const onClickContextMenu = (e) => {
        setShowContextMenuDialog(true);
        handleCloseContextMenu();
        setRightClickOption(e.target.id);
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handleCloseRightClickOptionsDialog = (isCancel = true) => {
        setShowContextMenuDialog(false);
        if (!isCancel) {
            const params = {
                field: sortModel.length > 0 ? sortModel[0].field : '',
                sort: sortModel.length > 0 ? sortModel[0].sort : '',
                page_size: paginationModel.pageSize,
                page_number: paginationModel.page
            };
            setQueryParams(params);
            // dispatch(updateSearchParams(params));
            // dispatch(getDevices(params));
        }
    };

    const updatePaginationModel = (newPaginationModel) => {
        setPaginationModel(newPaginationModel);
    };

    const getDevices = (item, type) => {
        const selectedStatements = viewStatements.filter((statement) => statement.view_id === item.id);
        setSelectedViewStatements(selectedStatements);
        setSelectedViewData(item);
        const initialStatements = selectedStatements.map((statement, index) => {
            return {
                id: index + 1,
                attribute: statement.deviceattrib,
                negative: statement.negative === 1 ? true : false,
                operator: statement.operator_id,
                value: statement.value,
                connector: index === selectedStatements.length - 1 ? '1' : statement.connector_id,
                operatorOptions: commonData.operators
            };
        });
        if (type === 'get') {
            setLoading(true);
            setSelectedItem(item.id);
            const timezoneOffSet = new Date().getTimezoneOffset();
            ApiHelper.post('/api/management/get_asset_views/', {
                viewStatements: initialStatements,
                page_number: paginationModel.page,
                page_size: paginationModel.pageSize,
                tz: -timezoneOffSet
            })
                .then((res) => {
                    setAssetViewsData(res.data.data);
                    setViewsTotalLength(res.data.total);
                    setLoading(false);
                })
                .catch();
        }
    };

    const handleSaveColumnOrders = () => {
        const columns_order = {};
        for (let i = 0; i < columnDefs.length; i++) {
            columns_order[columnDefs[i].field] = i + 1;
        }
        ApiHelper.put(`/api/management/device_columns/1/`, {
            status: 'device_columns_order',
            data: columns_order
        })
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data);
                setColumnDefs(
                    columns_order.map((c) => ({
                        ...c,
                        resizable: true,
                        headerClassName: 'device-column-header',
                        cellClassName: 'wrapCell'
                    }))
                );
                setDeviceColumnsData(columns_visibility);
                toast.success('Successfully saved.');
            })
            .catch(() => {
                toast.error('Failed to save the columns order');
            });
    };

    const handleCloseEditDeviceColumnsDialog = () => {
        setShowEditDeviceOrderColumns(false);
    };

    const handleSubmitDeviceColumnsVisibility = (data) => {
        setShowEditDeviceOrderColumns(false);
        ApiHelper.put(`/api/management/device_columns/1/`, {
            status: 'device_column_visibility',
            data: data
        })
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data);
                setColumnDefs(applyColumnStyle(columns_order));
                setDeviceColumnsData(columns_visibility);
                toast.success('Successfully updated.');
                const temp = [];
                columns_order.map((data) => {
                    temp.push({ label: data.headerName, key: data.field });
                });
                setCsvDownloadFileHeaders(temp);
            })
            .catch(() => {
                toast.error('Failed to update the device columns.');
            });
    };

    const handleChangeColumnOrder = (data) => {
        const { targetIndex, oldIndex } = data;
        const temp = columnDefs;
        const updatedVal = temp[oldIndex - 1];
        temp.splice(oldIndex - 1, 1);
        temp.splice(targetIndex - 1, 0, updatedVal);
        setColumnDefs(applyColumnStyle(temp));
        const headers = [];
        temp.map((data) => {
            headers.push({ label: data.headerName, key: data.field });
        });
        setCsvDownloadFileHeaders(headers);
    };

    return (
        <MainCard title="Views Management" cardHeaderTitle="All VM Devices" cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={3} style={{ display: 'flex', flexDirection: 'column' }}>
                    <SubCard title="View List">
                        {loading ? (
                            <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                Please wait...
                            </Typography>
                        ) : (
                            <Grid container>
                                <Paper
                                    sx={{
                                        width: '100%',
                                        backgroundColor: '#f1f2f4'
                                    }}
                                >
                                    {viewsList.length > 0 ? (
                                        <List>
                                            {viewsList.map((item, index) => (
                                                <ListItem
                                                    key={index}
                                                    sx={{
                                                        py: 0,
                                                        '&:not(:last-child)': {
                                                            borderBottom: '1px solid #eee'
                                                        },
                                                        cursor: 'pointer',
                                                        backgroundColor:
                                                            selectedItem === item.id ? theme.palette.primary.light : 'transparent'
                                                    }}
                                                >
                                                    <ListItemText
                                                        sx={{ cursor: 'pointer' }}
                                                        primary={
                                                            <Typography component="span" onClick={() => getDevices(item, 'get')}>
                                                                <FormControlLabel
                                                                    value={`${item.id}`}
                                                                    checked={selectedItem === item.id}
                                                                    //disabled
                                                                    control={<Radio />}
                                                                    label={
                                                                        <Typography sx={{ cursor: 'pointer' }} fontSize="16px">
                                                                            {item.name}
                                                                        </Typography>
                                                                    }
                                                                />
                                                            </Typography>
                                                        }
                                                    />
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="edit"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            getDevices(item, 'edit');
                                                            handleEditView(item);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenDialog(item.id);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Typography sx={{ textAlign: 'center', mt: 1 }} color={theme.palette.secondary.main}>
                                            There is no view created yet.
                                        </Typography>
                                    )}
                                </Paper>
                                <Dialog
                                    open={openDialog}
                                    onClose={handleCloseDialog}
                                    aria-labelledby="alert-dialog-title"
                                    aria-describedby="alert-dialog-description"
                                >
                                    <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
                                    <DialogContent>
                                        <DialogContentText id="alert-dialog-description">
                                            Are you sure you want to delete this view?
                                        </DialogContentText>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleCloseDialog}>Cancel</Button>
                                        <Button onClick={handleConfirmDelete} color="error">
                                            Delete
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                                <Grid item xs={12} sx={{ mt: 4 }}>
                                    <LoadingButton
                                        color="primary"
                                        variant="contained"
                                        sx={{ width: '100%' }}
                                        loading={loading}
                                        onClick={() => setShowAddViewDialog(true)}
                                    >
                                        Create New View
                                    </LoadingButton>
                                    {showAddViewDialog && (
                                        <AddViewDialog
                                            data={commonData}
                                            inputFields={inputFields}
                                            ruleStatementValues={ruleStatementValues}
                                            onClose={handleClose}
                                            onCloseCancel={() => setShowAddViewDialog(false)}
                                        />
                                    )}
                                    {showEditViewDialog && (
                                        <EditViewDialog
                                            onClose={handleClose}
                                            onCloseCancel={() => setShowEditViewDialog(false)}
                                            data={commonData}
                                            inputFields={inputFields}
                                            viewData={selectedViewData}
                                            selectedViewStatements={selectedViewStatements}
                                            ruleStatementValues={ruleStatementValues}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        )}
                    </SubCard>
                </Grid>
                <Grid item xs={9}>
                    <Grid
                        container
                        item
                        sx={{
                            padding: 1
                        }}
                    >
                        <Grid item sm={12} md={12} sx={{ textAlign: 'right' }}>
                            <Button
                                color="primary"
                                variant="contained"
                                sx={{ margin: '5px', borderRadius: '6px' }}
                                onClick={handleSaveColumnOrders}
                            >
                                Save Column Order
                            </Button>
                            <Button
                                color="primary"
                                variant="contained"
                                sx={{ margin: '5px', borderRadius: '6px' }}
                                onClick={() => setShowEditDeviceOrderColumns(true)}
                            >
                                Show/Hide Columns
                            </Button>
                            <Grid sx={{ float: 'right', marginTop: '5px' }}>
                                <ReportFileExports
                                    elementId="devices"
                                    downloadFileName="devices"
                                    downloadFileOption="csv"
                                    data={assetViewsData}
                                    headers={csvDownloadFileHeaders}
                                />
                            </Grid>
                            {showEditDeviceOrderColumns && (
                                <EditDeviceColumnsVisibilityDialog
                                    data={deviceColumnsData}
                                    onClose={handleCloseEditDeviceColumnsDialog}
                                    onSubmit={handleSubmitDeviceColumnsVisibility}
                                    columeData={totalDeviceColumns}
                                />
                            )}
                        </Grid>
                    </Grid>
                    {loading ? (
                        <div className="overlay">
                            <div className="spinner"></div>
                        </div>
                    ) : (
                        <Paper id="assetViews">
                            {!loading && assetViewsData.length === 0 ? (
                                <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                    No data for views
                                </Typography>
                            ) : (
                                <Box
                                    id="asset_devices"
                                    sx={{
                                        fontSize: '12px',
                                        '& .device-column-header': {
                                            color: 'black',
                                            fontWeight: 'bold'
                                        },
                                        height: 'calc(100vh - 248px)',
                                        width: '100%'
                                    }}
                                >
                                    <DataGridPro
                                        columns={columnDefs}
                                        rows={assetViewsData}
                                        rowCount={viewsTotalLength}
                                        rowHeight={30}
                                        ref={gridRef}
                                        getRowId={(row) => row.deviceid}
                                        pagination
                                        initialState={{
                                            pagination: {
                                                paginationModel
                                            }
                                        }}
                                        pageSizeOptions={[25, 100, 250, 500, 1000]}
                                        // pageSizeOptions={[3, 5, 10, 25, 50, 100, 250, 500, 1000]}
                                        checkboxSelection
                                        disableColumnMenu
                                        onColumnOrderChange={handleChangeColumnOrder}
                                        slotProps={{
                                            row: {
                                                onContextMenu: handleContextMenu,
                                                style: { cursor: 'context-menu' }
                                            },
                                            pagination: {
                                                showFirstButton: true,
                                                showLastButton: true
                                            }
                                        }}
                                        onRowSelectionModelChange={handleSelectionChange}
                                        sortingMode="server"
                                        sortModel={sortModel}
                                        paginationMode={'server'}
                                        onSortModelChange={handleSortModelChange}
                                        rowSelectionModel={Array.from(selectedRows)}
                                        loading={loading}
                                        onPaginationModelChange={updatePaginationModel}
                                        disableRowHeightAdjustment={false}
                                        getRowHeight={() => 30}
                                        sx={{
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: '#f5f5f5', // Optional: Customize header background
                                                position: 'sticky', // Ensures headers are sticky
                                                top: 0,
                                                zIndex: 1 // Ensures headers stay above content when scrolling
                                            },
                                            '& .MuiDataGrid-virtualScroller': {
                                                overflowY: 'auto',
                                                overflowX: 'auto' // Enable horizontal scrolling
                                            },
                                            '& .MuiDataGrid-columnHeaderTitleContainer': {
                                                display: 'flex',
                                                justifyContent: 'center', // Center text horizontally
                                                alignItems: 'center', // Center text vertically
                                                height: '100%'
                                            }
                                        }}
                                    />
                                    <Menu
                                        open={contextMenu !== null}
                                        onClose={handleCloseContextMenu}
                                        anchorReference="anchorPosition"
                                        anchorPosition={
                                            contextMenu !== null
                                                ? {
                                                      top: contextMenu.mouseY,
                                                      left: contextMenu.mouseX
                                                  }
                                                : undefined
                                        }
                                        componentsProps={{
                                            root: {
                                                onContextMenu: (e) => {
                                                    e.preventDefault();
                                                    handleCloseContextMenu();
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem id="wake" onClick={onClickContextMenu}>
                                            Wake
                                        </MenuItem>
                                        <MenuItem id="sleep" onClick={onClickContextMenu}>
                                            Sleep
                                        </MenuItem>
                                        <MenuItem id="restart" onClick={onClickContextMenu}>
                                            Restart
                                        </MenuItem>
                                        <MenuItem id="shutdown" onClick={onClickContextMenu}>
                                            ShutDown
                                        </MenuItem>
                                        <MenuItem id="group" onClick={onClickContextMenu}>
                                            Assign Group
                                        </MenuItem>
                                        <MenuItem id="policy" onClick={onClickContextMenu}>
                                            Assign Policy
                                        </MenuItem>
                                        <Divider />
                                        <MenuItem id="label" onClick={onClickContextMenu}>
                                            Assign Label
                                        </MenuItem>
                                        {userInfo.admin_permissions.root_admin && (
                                            <MenuItem id="setBaseline" onClick={onClickContextMenu}>
                                                Set Baseline
                                            </MenuItem>
                                        )}
                                        <Divider />
                                        {userInfo.admin_permissions.root_admin && (
                                            <MenuItem id="getLogFile" onClick={onClickContextMenu}>
                                                Get Log File
                                            </MenuItem>
                                        )}
                                        <MenuItem id="upgrade" onClick={onClickContextMenu}>
                                            Upgrade/No Upgrade
                                        </MenuItem>
                                        <MenuItem id="license" onClick={onClickContextMenu}>
                                            License/Unlicensed
                                        </MenuItem>
                                        {userInfo.admin_permissions.org_admin && (
                                            <MenuItem id="getTISData" onClick={onClickContextMenu}>
                                                Get TIS Data
                                            </MenuItem>
                                        )}
                                        {userInfo.admin_permissions.org_admin && isScriptAvailable && (
                                            <MenuItem id="script" onClick={onClickContextMenu}>
                                                Run Script
                                            </MenuItem>
                                        )}
                                    </Menu>
                                </Box>
                            )}
                            {showContextMenuDialog && (
                                <RightClickOptionsDialog
                                    option={rightClickOption}
                                    onClose={handleCloseRightClickOptionsDialog}
                                    devices={selectedViews}
                                    //inputFields={limitLength}
                                />
                            )}
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default ManagementViews;
